// src/pages/StubPages.jsx
import React, { useEffect, useState } from 'react';
import Sidebar from '../components/shared/Sidebar';
import { useAuth } from '../hooks/useAuth';
import {
  subscribeTasks, subscribeMyTasks, subscribeUsers, subscribeActivity,
  createTask, updateTask, deleteTask,
  reassignTask as doReassign,
  logActivity,
  subscribeSettingsDoc, saveSettingsDoc,
} from '../firebase/firestore';

// ─── Page shell ───────────────────────────────────────────────
function Page({ title, sub, children }) {
  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">{title}</div>
            <div className="page-sub">{sub}</div>
          </div>
        </div>
        <div className="scroll">{children}</div>
      </div>
    </div>
  );
}

// ─── Status helpers ───────────────────────────────────────────
const STATUS_OPTS = ['pending', 'in-progress', 'review', 'urgent', 'done'];

const STATUS_PILLS = [
  { key:'all',         label:'All' },
  { key:'urgent',      label:'Urgent' },
  { key:'in-progress', label:'In progress' },
  { key:'review',      label:'Review' },
  { key:'pending',     label:'Pending' },
  { key:'done',        label:'Done' },
];

const PILL_COLOR = {
  urgent:        { bg:'#FEF2F2', color:'#DC2626', border:'#FECACA' },
  'in-progress': { bg:'#EFF6FF', color:'#2563EB', border:'#BFDBFE' },
  review:        { bg:'#FDF4FF', color:'#9333EA', border:'#E9D5FF' },
  pending:       { bg:'#FFFBEB', color:'#D97706', border:'#FDE68A' },
  done:          { bg:'#F0FDF4', color:'#16A34A', border:'#BBF7D0' },
  all:           { bg:'var(--gray-100)', color:'var(--gray-700)', border:'var(--gray-200)' },
};

const badgeClass  = s => s === 'done' ? 'done' : s === 'urgent' ? 'urgent' : 'progress';
const borderColor = s => s === 'urgent' ? 'var(--red)' : s === 'done' ? 'var(--green)' : 'var(--blue)';
const labelFor    = s => s ? s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ') : '—';

// ─── Shared: TaskModal ────────────────────────────────────────
const EMPTY_TASK = { title:'', description:'', assigneeId:'', assigneeName:'', status:'pending', dueDate:'' };

function TaskModal({ task, users, profile, onClose, onSave, onDelete, allowAssign = true }) {
  const [form, setForm] = useState(task
    ? { title:task.title||'', description:task.description||'', assigneeId:task.assigneeId||'',
        assigneeName:task.assigneeName||'', status:task.status||'pending', dueDate:task.dueDate||'' }
    : { ...EMPTY_TASK, assigneeId:profile?.uid||'', assigneeName:profile?.name||'' }
  );
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleAssignee = (uid) => {
    const u = users.find(u => (u.uid || u.id) === uid);
    setForm(p => ({ ...p, assigneeId: uid, assigneeName: u?.name || '' }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { alert('Task title is required.'); return; }
    if (allowAssign && !form.assigneeId) { alert('Please assign this task to a team member.'); return; }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!task || !onDelete) return;
    if (!window.confirm(`Delete "${task.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try { await onDelete(task.id); } finally { setDeleting(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-header">
          <span className="modal-title">
            <i className={`ti ${task ? 'ti-edit' : 'ti-plus'}`} style={{ color:'var(--blue)', marginRight:8 }} />
            {task ? 'Edit task' : 'New task'}
          </span>
          <button className="btn sm" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div className="form-grid">
          <div className="form-group full">
            <label>Title *</label>
            <input type="text" autoFocus value={form.title}
              onChange={e => setForm(p => ({ ...p, title:e.target.value }))}
              placeholder="e.g. Clear customs for MSC shipment" />
          </div>
          <div className="form-group full">
            <label>Description</label>
            <textarea rows={3} value={form.description}
              onChange={e => setForm(p => ({ ...p, description:e.target.value }))}
              placeholder="Details, context, or instructions…" />
          </div>
          {allowAssign && (
            <div className="form-group">
              <label>Assigned to *</label>
              <select value={form.assigneeId} onChange={e => handleAssignee(e.target.value)}>
                <option value="">Select team member…</option>
                {users.map(u => {
                  const uid = u.uid || u.id;
                  return (
                    <option key={uid} value={uid}>
                      {u.name}{uid === profile?.uid ? ' (you)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
          <div className="form-group">
            <label>Status</label>
            <select value={form.status} onChange={e => setForm(p => ({ ...p, status:e.target.value }))}>
              {STATUS_OPTS.map(s => <option key={s} value={s}>{labelFor(s)}</option>)}
            </select>
          </div>
          <div className="form-group full">
            <label>Due date</label>
            <input type="date" value={form.dueDate||''} onChange={e => setForm(p => ({ ...p, dueDate:e.target.value }))} />
          </div>
        </div>
        <div className="modal-footer">
          {onDelete && task && (
            <button className="btn danger" onClick={handleDelete} disabled={deleting} style={{ marginRight:'auto' }}>
              <i className="ti ti-trash" /> {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSave} disabled={saving}>
            <i className="ti ti-device-floppy" /> {saving ? 'Saving…' : 'Save task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared: StatusModal (quick status update) ────────────────
function StatusModal({ task, onClose, onSave }) {
  const [status, setStatus] = useState(task.status || 'pending');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try { await onSave(status); } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:320 }}>
        <div className="modal-header">
          <span className="modal-title"><i className="ti ti-refresh" style={{ color:'var(--blue)', marginRight:8 }} />Update status</span>
          <button className="btn sm" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:12, color:'var(--gray-700)' }}>{task.title}</div>
        <div className="form-group">
          <label>New status</label>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            {STATUS_OPTS.map(s => <option key={s} value={s}>{labelFor(s)}</option>)}
          </select>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Update status'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared: ReassignModal ────────────────────────────────────
function ReassignModal({ task, users, onClose, onSave }) {
  const [assigneeId, setAssigneeId] = useState('');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    const u = users.find(u => (u.uid || u.id) === assigneeId);
    if (!assigneeId || !u) { alert('Please select a team member.'); return; }
    setSaving(true);
    try { await onSave(assigneeId, u.name); } finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:380 }}>
        <div className="modal-header">
          <span className="modal-title"><i className="ti ti-user-check" style={{ color:'var(--blue)', marginRight:8 }} />Reassign task</span>
          <button className="btn sm" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div style={{ padding:'8px 0 12px', fontSize:13, color:'var(--gray-700)' }}>
          <strong>{task.title}</strong>
          {task.assigneeName && <div style={{ fontSize:11, color:'var(--gray-500)', marginTop:4 }}>Currently: {task.assigneeName}</div>}
        </div>
        <div className="form-group">
          <label>Assign to *</label>
          <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}>
            <option value="">Select team member…</option>
            {users.map(u => {
              const uid = u.uid || u.id;
              return <option key={uid} value={uid}>{u.name}</option>;
            })}
          </select>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSave} disabled={saving}>
            <i className="ti ti-user-check" /> {saving ? 'Reassigning…' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// ADMIN: All Tasks
// ═══════════════════════════════════════════════════════════════
export function AdminTasks() {
  const { profile } = useAuth();
  const [tasks,          setTasks]         = useState([]);
  const [users,          setUsers]         = useState([]);
  const [statusFilter,   setStatusFilter]  = useState('all');
  const [memberFilter,   setMemberFilter]  = useState('all');
  const [search,         setSearch]        = useState('');
  const [taskModal,      setTaskModal]     = useState(null); // null | 'new' | task-obj
  const [reassignTarget, setReassignTarget] = useState(null); // null | task-obj

  useEffect(() => {
    const u1 = subscribeTasks(setTasks);
    const u2 = subscribeUsers(setUsers);
    return () => { u1(); u2(); };
  }, []);

  const members = [...new Set(tasks.map(t => t.assigneeName).filter(Boolean))];

  const visible = tasks.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (memberFilter !== 'all' && t.assigneeName !== memberFilter) return false;
    if (search && !`${t.title} ${t.description||''} ${t.assigneeName||''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const saveTask = async (form) => {
    if (taskModal === 'new') {
      const ref = await createTask({ ...form, createdBy:profile?.uid, createdByName:profile?.name });
      await logActivity({ userId:profile?.uid, userName:profile?.name, action:'create', entityType:'task', entityId:ref.id, message:`Created task "${form.title}" → ${form.assigneeName||'unassigned'}` });
    } else {
      await updateTask(taskModal.id, form);
      await logActivity({ userId:profile?.uid, userName:profile?.name, action:'update', entityType:'task', entityId:taskModal.id, message:`Updated task "${form.title}"` });
    }
    setTaskModal(null);
  };

  const handleDelete = async (taskId) => {
    const t = tasks.find(x => x.id === taskId);
    await deleteTask(taskId);
    await logActivity({ userId:profile?.uid, userName:profile?.name, action:'delete', entityType:'task', entityId:taskId, message:`Deleted task "${t?.title}"` });
    setTaskModal(null);
  };

  const handleReassign = async (assigneeId, assigneeName) => {
    await doReassign(reassignTarget.id, assigneeId, assigneeName, profile?.uid, profile?.name);
    await logActivity({ userId:profile?.uid, userName:profile?.name, action:'update', entityType:'task', entityId:reassignTarget.id, message:`Reassigned "${reassignTarget.title}" to ${assigneeName}` });
    setReassignTarget(null);
  };

  return (
    <Page title="All tasks" sub="Manage and reassign tasks across the team">
      {/* Filter bar */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:10, alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative' }}>
          <i className="ti ti-search" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--gray-400)', fontSize:14 }} />
          <input type="text" placeholder="Search tasks…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding:'7px 12px 7px 32px', borderRadius:8, border:'1px solid var(--gray-200)', fontSize:13, width:200, outline:'none' }} />
        </div>
        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
          {STATUS_PILLS.map(p => {
            const active = statusFilter === p.key;
            const c = PILL_COLOR[p.key];
            return (
              <button key={p.key} onClick={() => setStatusFilter(p.key)} style={{
                padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer',
                border:`1px solid ${active ? c.border : 'var(--gray-200)'}`,
                background: active ? c.bg : '#fff',
                color: active ? c.color : 'var(--gray-500)',
                transition:'all .15s',
              }}>{p.label}</button>
            );
          })}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <i className="ti ti-user" style={{ fontSize:14, color:'var(--gray-400)' }} />
          <select value={memberFilter} onChange={e => setMemberFilter(e.target.value)}
            style={{ fontSize:12, padding:'5px 10px', borderRadius:8, border:'1px solid var(--gray-200)', background:'#fff', cursor:'pointer', outline:'none' }}>
            <option value="all">All members</option>
            {members.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <span style={{ fontSize:12, color:'var(--gray-400)' }}>{visible.length} task{visible.length !== 1 ? 's' : ''}</span>
        <button className="btn primary" style={{ marginLeft:'auto' }} onClick={() => setTaskModal('new')}>
          <i className="ti ti-plus" /> New task
        </button>
      </div>

      <div className="card">
        {visible.length === 0
          ? <div className="empty-state"><i className="ti ti-search" /><p>No tasks match the selected filters.</p></div>
          : <div className="table-wrap"><table>
              <thead><tr><th>Title</th><th>Assigned to</th><th>Status</th><th>Due date</th><th>Actions</th></tr></thead>
              <tbody>{visible.map(t => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.title}</strong>
                    {t.description && <div style={{ fontSize:11, color:'var(--gray-500)' }}>{t.description}</div>}
                  </td>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <div style={{ width:24, height:24, borderRadius:'50%', background:'var(--blue-light)', color:'var(--blue)', fontSize:9, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {t.assigneeName?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() || '?'}
                      </div>
                      {t.assigneeName || '—'}
                    </div>
                  </td>
                  <td><span className={`badge ${badgeClass(t.status)}`}>{t.status}</span></td>
                  <td style={{ fontSize:12 }}>{t.dueDate || '—'}</td>
                  <td>
                    <div className="btn-group">
                      <button className="btn sm" onClick={() => setTaskModal(t)}><i className="ti ti-edit" /> Edit</button>
                      <button className="btn sm primary" onClick={() => setReassignTarget(t)}><i className="ti ti-user-check" /> Reassign</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>}
      </div>

      {taskModal && (
        <TaskModal
          task={taskModal === 'new' ? null : taskModal}
          users={users}
          profile={profile}
          onClose={() => setTaskModal(null)}
          onSave={saveTask}
          onDelete={taskModal !== 'new' ? handleDelete : null}
          allowAssign={true}
        />
      )}
      {reassignTarget && (
        <ReassignModal
          task={reassignTarget}
          users={users}
          onClose={() => setReassignTarget(null)}
          onSave={handleReassign}
        />
      )}
    </Page>
  );
}

// ═══════════════════════════════════════════════════════════════
// STAFF: My Tasks
// ═══════════════════════════════════════════════════════════════
export function StaffTasks() {
  const { profile } = useAuth();
  const [myTasks,     setMyTasks]    = useState([]);
  const [users,       setUsers]      = useState([]);
  const [taskModal,   setTaskModal]  = useState(null); // null | 'new' | task-obj
  const [statusModal, setStatusModal] = useState(null); // null | task-obj

  useEffect(() => {
    if (!profile?.uid) return;
    const u1 = subscribeMyTasks(profile.uid, setMyTasks);
    const u2 = subscribeUsers(data => setUsers(data));
    return () => { u1(); u2(); };
  }, [profile?.uid]);

  const saveTask = async (form) => {
    if (taskModal === 'new') {
      const data = { ...form, assigneeId: profile?.uid, assigneeName: profile?.name, createdBy: profile?.uid, createdByName: profile?.name };
      const ref = await createTask(data);
      await logActivity({ userId:profile?.uid, userName:profile?.name, action:'create', entityType:'task', entityId:ref.id, message:`Created task "${form.title}"` });
    } else {
      await updateTask(taskModal.id, form);
      await logActivity({ userId:profile?.uid, userName:profile?.name, action:'update', entityType:'task', entityId:taskModal.id, message:`Updated task "${form.title}"` });
    }
    setTaskModal(null);
  };

  const handleDelete = async (taskId) => {
    const t = myTasks.find(x => x.id === taskId);
    await deleteTask(taskId);
    await logActivity({ userId:profile?.uid, userName:profile?.name, action:'delete', entityType:'task', entityId:taskId, message:`Deleted task "${t?.title}"` });
    setTaskModal(null);
  };

  const handleStatusUpdate = async (newStatus) => {
    await updateTask(statusModal.id, { ...statusModal, status: newStatus });
    await logActivity({ userId:profile?.uid, userName:profile?.name, action:'update', entityType:'task', entityId:statusModal.id, message:`Updated "${statusModal.title}" status to ${newStatus}` });
    setStatusModal(null);
  };

  return (
    <Page title="My tasks" sub="Tasks assigned to you">
      <div className="card">
        <div className="card-header">
          <span className="card-title"><i className="ti ti-user-check" style={{ color:'var(--blue)' }} /> My tasks</span>
          <button className="btn primary" onClick={() => setTaskModal('new')}><i className="ti ti-plus" /> New task</button>
        </div>
        {myTasks.length === 0
          ? <div className="empty-state"><i className="ti ti-checklist" /><p>No tasks assigned to you yet.</p></div>
          : myTasks.map(t => (
            <div key={t.id} style={{ border:'1px solid var(--gray-200)', borderLeft:`3px solid ${borderColor(t.status)}`, borderRadius:'0 8px 8px 0', padding:12, marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:3 }}>{t.title}</div>
                  {t.description && <div style={{ fontSize:12, color:'var(--gray-600)', marginBottom:6 }}>{t.description}</div>}
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    <span className={`badge ${badgeClass(t.status)}`}>{t.status}</span>
                    {t.dueDate && <span style={{ fontSize:11, color:'var(--gray-500)' }}>Due {t.dueDate}</span>}
                  </div>
                </div>
                <div className="btn-group">
                  <button className="btn sm" onClick={() => setTaskModal(t)}><i className="ti ti-edit" /> Edit</button>
                  <button className="btn sm primary" onClick={() => setStatusModal(t)}><i className="ti ti-refresh" /> Update</button>
                </div>
              </div>
            </div>
          ))}
      </div>

      {taskModal && (
        <TaskModal
          task={taskModal === 'new' ? null : taskModal}
          users={users}
          profile={profile}
          onClose={() => setTaskModal(null)}
          onSave={saveTask}
          onDelete={taskModal !== 'new' ? handleDelete : null}
          allowAssign={taskModal === 'new'}
        />
      )}
      {statusModal && (
        <StatusModal task={statusModal} onClose={() => setStatusModal(null)} onSave={handleStatusUpdate} />
      )}
    </Page>
  );
}

// ═══════════════════════════════════════════════════════════════
// STAFF: All Tasks
// ═══════════════════════════════════════════════════════════════
export function AllTasks() {
  const { profile } = useAuth();
  const [allTasks,     setAllTasks]    = useState([]);
  const [users,        setUsers]       = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [memberFilter, setMemberFilter] = useState('all');
  const [taskModal,    setTaskModal]   = useState(null); // null | 'new' | task-obj (own only)
  const [statusModal,  setStatusModal] = useState(null); // null | task-obj

  useEffect(() => {
    const u1 = subscribeTasks(setAllTasks);
    const u2 = subscribeUsers(data => setUsers(data));
    return () => { u1(); u2(); };
  }, []);

  const members = Array.from(
    new Map(allTasks.map(t => [t.assigneeId, t.assigneeName])).entries()
  ).map(([id, name]) => ({ id, name }));

  const visible = allTasks.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (memberFilter !== 'all' && t.assigneeId !== memberFilter) return false;
    return true;
  });

  const isMine = t => t.assigneeId === profile?.uid || t.assigneeId === 'me';

  const saveTask = async (form) => {
    if (taskModal === 'new') {
      const ref = await createTask({ ...form, createdBy:profile?.uid, createdByName:profile?.name });
      await logActivity({ userId:profile?.uid, userName:profile?.name, action:'create', entityType:'task', entityId:ref.id, message:`Created task "${form.title}" → ${form.assigneeName||'unassigned'}` });
    } else {
      await updateTask(taskModal.id, form);
      await logActivity({ userId:profile?.uid, userName:profile?.name, action:'update', entityType:'task', entityId:taskModal.id, message:`Updated task "${form.title}"` });
    }
    setTaskModal(null);
  };

  const handleDelete = async (taskId) => {
    const t = allTasks.find(x => x.id === taskId);
    await deleteTask(taskId);
    await logActivity({ userId:profile?.uid, userName:profile?.name, action:'delete', entityType:'task', entityId:taskId, message:`Deleted task "${t?.title}"` });
    setTaskModal(null);
  };

  const handleStatusUpdate = async (newStatus) => {
    await updateTask(statusModal.id, { ...statusModal, status: newStatus });
    await logActivity({ userId:profile?.uid, userName:profile?.name, action:'update', entityType:'task', entityId:statusModal.id, message:`Updated "${statusModal.title}" status to ${newStatus}` });
    setStatusModal(null);
  };

  return (
    <Page title="All tasks" sub="Your tasks and the whole team's tasks">
      {/* Filter bar */}
      <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:10, marginBottom:16 }}>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {STATUS_PILLS.map(p => {
            const active = statusFilter === p.key;
            const c = PILL_COLOR[p.key];
            return (
              <button key={p.key} onClick={() => setStatusFilter(p.key)} style={{
                padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer',
                border:`1px solid ${active ? c.border : 'var(--gray-200)'}`,
                background: active ? c.bg : '#fff',
                color: active ? c.color : 'var(--gray-500)',
                transition:'all .15s',
              }}>{p.label}</button>
            );
          })}
        </div>
        <div style={{ flex:1 }} />
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <i className="ti ti-users" style={{ fontSize:14, color:'var(--gray-400)' }} />
          <select value={memberFilter} onChange={e => setMemberFilter(e.target.value)}
            style={{ fontSize:12, padding:'5px 10px', borderRadius:8, border:'1px solid var(--gray-200)', background:'#fff', color:'var(--gray-700)', cursor:'pointer', outline:'none' }}>
            <option value="all">All members</option>
            {members.map(m => (
              <option key={m.id} value={m.id}>
                {m.id === 'me' || m.id === profile?.uid ? 'Me' : m.name}
              </option>
            ))}
          </select>
        </div>
        <button className="btn primary" onClick={() => setTaskModal('new')}><i className="ti ti-plus" /> New task</button>
        <span style={{ fontSize:12, color:'var(--gray-400)', whiteSpace:'nowrap' }}>{visible.length} task{visible.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="card" style={{ padding:'0 16px' }}>
        {visible.length === 0
          ? <div className="empty-state"><i className="ti ti-search" /><p>No tasks match the selected filters.</p></div>
          : visible.map(t => {
            const mine = isMine(t);
            if (mine) return (
              <div key={t.id} style={{ border:'1px solid var(--gray-200)', borderLeft:`3px solid ${borderColor(t.status)}`, borderRadius:'0 8px 8px 0', padding:12, marginBottom:8, marginTop:8 }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, marginBottom:3 }}>{t.title}</div>
                    {t.description && <div style={{ fontSize:12, color:'var(--gray-600)', marginBottom:6 }}>{t.description}</div>}
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <span className={`badge ${badgeClass(t.status)}`}>{t.status}</span>
                      {t.dueDate && <span style={{ fontSize:11, color:'var(--gray-500)' }}>Due {t.dueDate}</span>}
                    </div>
                  </div>
                  <div className="btn-group">
                    <button className="btn sm" onClick={() => setTaskModal(t)}><i className="ti ti-edit" /> Edit</button>
                    <button className="btn sm primary" onClick={() => setStatusModal(t)}><i className="ti ti-refresh" /> Update</button>
                  </div>
                </div>
              </div>
            );
            return (
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--gray-200)' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--gray-100)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'var(--gray-500)', flexShrink:0 }}>
                  {t.assigneeName?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() || '?'}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>{t.title}</div>
                  <div style={{ fontSize:11, color:'var(--gray-500)' }}>{t.assigneeName}</div>
                </div>
                <span className={`badge ${badgeClass(t.status)}`}>{t.status}</span>
                {t.dueDate && <span style={{ fontSize:11, color:'var(--gray-500)', whiteSpace:'nowrap' }}>Due {t.dueDate}</span>}
                <i className="ti ti-lock" style={{ fontSize:14, color:'var(--gray-300)' }} />
              </div>
            );
          })}
      </div>

      {taskModal && (
        <TaskModal
          task={taskModal === 'new' ? null : taskModal}
          users={users}
          profile={profile}
          onClose={() => setTaskModal(null)}
          onSave={saveTask}
          onDelete={taskModal !== 'new' ? handleDelete : null}
          allowAssign={true}
        />
      )}
      {statusModal && (
        <StatusModal task={statusModal} onClose={() => setStatusModal(null)} onSave={handleStatusUpdate} />
      )}
    </Page>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADMIN: Activity Log
// ═══════════════════════════════════════════════════════════════
const ACTION_ICON  = { create:'ti-plus-circle', update:'ti-edit-circle', delete:'ti-trash-x', login:'ti-login' };
const ACTION_COLOR = { create:'#22C55E', update:'#185FA5', delete:'#EF4444', login:'#6B7280' };

export function AdminActivityLog() {
  const [activity,    setActivity]    = useState([]);
  const [userFilter,  setUserFilter]  = useState('all');
  const [typeFilter,  setTypeFilter]  = useState('all');
  const [search,      setSearch]      = useState('');

  useEffect(() => {
    const unsub = subscribeActivity(setActivity);
    return unsub;
  }, []);

  const users = [...new Set(activity.map(a => a.userName).filter(Boolean))];

  const visible = activity.filter(a => {
    if (userFilter !== 'all' && a.userName !== userFilter) return false;
    if (typeFilter !== 'all' && a.action !== typeFilter) return false;
    if (search && !a.message?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const initials = name => name?.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';

  const timeLabel = (a) => {
    if (a.timestamp?.toDate) {
      return a.timestamp.toDate().toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
    }
    return a._time || '—';
  };

  return (
    <Page title="Activity log" sub="Every action taken by every team member">
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative' }}>
          <i className="ti ti-search" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--gray-400)', fontSize:14 }} />
          <input type="text" placeholder="Search activity…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding:'7px 12px 7px 32px', borderRadius:8, border:'1px solid var(--gray-200)', fontSize:13, width:200, outline:'none' }} />
        </div>
        <select value={userFilter} onChange={e => setUserFilter(e.target.value)}
          style={{ fontSize:12, padding:'6px 10px', borderRadius:8, border:'1px solid var(--gray-200)', background:'#fff', outline:'none' }}>
          <option value="all">All members</option>
          {users.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ fontSize:12, padding:'6px 10px', borderRadius:8, border:'1px solid var(--gray-200)', background:'#fff', outline:'none' }}>
          <option value="all">All actions</option>
          <option value="create">Created</option>
          <option value="update">Updated</option>
          <option value="delete">Deleted</option>
        </select>
        <span style={{ fontSize:12, color:'var(--gray-400)', marginLeft:'auto' }}>{visible.length} entries</span>
      </div>

      <div className="card" style={{ padding:'0 16px' }}>
        {visible.length === 0
          ? <div className="empty-state"><i className="ti ti-activity" /><p>No activity matches the filters.</p></div>
          : visible.map((a, i) => {
            const color = ACTION_COLOR[a.action] || '#6B7280';
            const icon  = ACTION_ICON[a.action]  || 'ti-circle';
            return (
              <div key={a.id} style={{ display:'flex', gap:12, padding:'12px 0', borderBottom: i < visible.length-1 ? '1px solid var(--gray-200)' : 'none', alignItems:'flex-start' }}>
                <div style={{ position:'relative', flexShrink:0 }}>
                  <div className="avatar" style={{ background:color+'20', color, width:36, height:36, fontSize:10, fontWeight:700 }}>
                    {initials(a.userName)}
                  </div>
                  <div style={{ position:'absolute', bottom:-2, right:-2, width:16, height:16, borderRadius:'50%', background:color, display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid #fff' }}>
                    <i className={`ti ${icon}`} style={{ fontSize:8, color:'#fff' }} />
                  </div>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, color:'var(--gray-900)', lineHeight:1.5 }}>
                    <strong>{a.userName}</strong> {a.message}
                  </div>
                  <div style={{ display:'flex', gap:8, marginTop:4, alignItems:'center' }}>
                    <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:color+'15', color, fontWeight:600 }}>
                      {a.entityType || 'system'}
                    </span>
                    <span style={{ fontSize:11, color:'var(--gray-400)' }}>{timeLabel(a)}</span>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </Page>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADMIN: Roles & Access
// ═══════════════════════════════════════════════════════════════
const FEATURES = [
  { key:'view_dashboard',   label:'View dashboard'        },
  { key:'view_all_tasks',   label:'View all tasks'        },
  { key:'create_tasks',     label:'Create tasks'          },
  { key:'edit_own_tasks',   label:'Edit own tasks'        },
  { key:'delete_tasks',     label:'Delete tasks'          },
  { key:'reassign_tasks',   label:'Reassign tasks'        },
  { key:'view_shipments',   label:'View shipments'        },
  { key:'edit_shipments',   label:'Add / edit shipments'  },
  { key:'delete_shipments', label:'Delete shipments'      },
  { key:'export_excel',     label:'Export to Excel'       },
  { key:'view_analytics',   label:'View analytics'        },
  { key:'manage_users',     label:'Manage users'          },
  { key:'change_roles',     label:'Change roles & access' },
  { key:'view_activity',    label:'View activity log'     },
  { key:'access_settings',  label:'Access settings'       },
];

const DEFAULT_PERMS = {
  view_dashboard:true, view_all_tasks:true, create_tasks:true, edit_own_tasks:true,
  delete_tasks:false, reassign_tasks:false, view_shipments:true, edit_shipments:true,
  delete_shipments:false, export_excel:true, view_analytics:false, manage_users:false,
  change_roles:false, view_activity:false, access_settings:false,
};

function ToggleSwitch({ on, onChange, disabled }) {
  return (
    <div onClick={disabled ? undefined : onChange} style={{
      width:36, height:20, borderRadius:10, flexShrink:0,
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: on ? 'var(--blue)' : 'var(--gray-200)',
      position:'relative', transition:'background .2s',
      opacity: disabled ? 0.5 : 1,
    }}>
      <div style={{ width:16, height:16, borderRadius:'50%', background:'#fff', position:'absolute', top:2, left: on ? 18 : 2, transition:'left .2s', boxShadow:'0 1px 3px rgba(0,0,0,.3)' }} />
    </div>
  );
}

export function AdminRoles() {
  const [perms,   setPerms]   = useState({ staff: { ...DEFAULT_PERMS } });
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeSettingsDoc('roles', data => {
      if (data?.staff) setPerms(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const toggle = (key) => {
    setPerms(p => ({ ...p, staff: { ...p.staff, [key]: !p.staff[key] } }));
  };

  const save = async () => {
    setSaving(true);
    try { await saveSettingsDoc('roles', perms); } catch (_) {}
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (loading) return <Page title="Roles & access" sub="Loading…"><div className="empty-state"><div className="spinner" /></div></Page>;

  return (
    <Page title="Roles & access" sub="Control what each role can do across the system">
      <div className="card">
        <div className="card-header">
          <span className="card-title"><i className="ti ti-shield-lock" style={{ color:'var(--blue)' }} /> Permission matrix</span>
          <button className="btn primary" onClick={save} disabled={saving}>
            {saved ? <><i className="ti ti-check" /> Saved!</> : <><i className="ti ti-device-floppy" /> {saving ? 'Saving…' : 'Save changes'}</>}
          </button>
        </div>
        <div className="alert info" style={{ marginBottom:16 }}>
          <i className="ti ti-info-circle" />
          <span>Admin permissions are always on and cannot be changed. Toggle staff permissions below.</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width:'55%' }}>Feature</th>
                <th style={{ textAlign:'center' }}><span className="badge admin">Admin</span></th>
                <th style={{ textAlign:'center' }}><span className="badge staff">Staff</span></th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map(f => (
                <tr key={f.key}>
                  <td style={{ fontSize:13 }}>{f.label}</td>
                  <td style={{ textAlign:'center' }}><ToggleSwitch on={true} disabled={true} /></td>
                  <td style={{ textAlign:'center' }}>
                    <ToggleSwitch on={!!perms.staff?.[f.key]} onChange={() => toggle(f.key)} disabled={false} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Page>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADMIN: Settings
// ═══════════════════════════════════════════════════════════════
const DEFAULT_COMPANY = { name:'GACH SERVICES LTD', tagline:'Logistics & Customs Clearing', timezone:'Africa/Kampala', currency:'UGX' };
const DEFAULT_NOTIFS  = { taskAssign:true, taskOverdue:true, shipmentStatus:true, dailyDigest:false, weeklyReport:true };

export function AdminSettings() {
  const [tab,     setTab]     = useState('general');
  const [company, setCompany] = useState(DEFAULT_COMPANY);
  const [notifs,  setNotifs]  = useState(DEFAULT_NOTIFS);
  const [saving,  setSaving]  = useState('');
  const [saved,   setSaved]   = useState('');

  useEffect(() => {
    const u1 = subscribeSettingsDoc('company',       data => { if (data) setCompany(p => ({ ...p, ...data })); });
    const u2 = subscribeSettingsDoc('notifications', data => { if (data) setNotifs(p  => ({ ...p, ...data })); });
    return () => { u1(); u2(); };
  }, []);

  const save = async (data, key) => {
    setSaving(key);
    try { await saveSettingsDoc(key, data); } catch (_) {}
    setSaving('');
    setSaved(key);
    setTimeout(() => setSaved(''), 2500);
  };

  const TABS = [
    { key:'general',       icon:'ti-building',      label:'General'       },
    { key:'notifications', icon:'ti-bell',           label:'Notifications' },
    { key:'danger',        icon:'ti-alert-triangle', label:'Danger zone'   },
  ];

  return (
    <Page title="Settings" sub="System-wide configuration">
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'1px solid var(--gray-200)' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:'9px 18px', fontSize:13, background:'none', border:'none', cursor:'pointer',
            borderBottom: tab===t.key ? '2.5px solid var(--blue)' : '2.5px solid transparent',
            color: tab===t.key ? 'var(--blue)' : 'var(--gray-600)',
            fontWeight: tab===t.key ? 600 : 400,
            display:'flex', alignItems:'center', gap:7,
          }}>
            <i className={`ti ${t.icon}`} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title"><i className="ti ti-building" style={{ color:'var(--blue)' }} /> Company information</span>
          </div>
          <div className="form-grid">
            <div className="form-group full">
              <label>Company name</label>
              <input type="text" value={company.name} onChange={e => setCompany(p=>({...p,name:e.target.value}))} />
            </div>
            <div className="form-group full">
              <label>Tagline / description</label>
              <input type="text" value={company.tagline} onChange={e => setCompany(p=>({...p,tagline:e.target.value}))} />
            </div>
            <div className="form-group">
              <label>Timezone</label>
              <select value={company.timezone} onChange={e => setCompany(p=>({...p,timezone:e.target.value}))}>
                <option value="Africa/Kampala">Africa/Kampala (EAT, UTC+3)</option>
                <option value="Africa/Nairobi">Africa/Nairobi (EAT, UTC+3)</option>
                <option value="Africa/Dar_es_Salaam">Africa/Dar es Salaam (EAT, UTC+3)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Currency</label>
              <select value={company.currency} onChange={e => setCompany(p=>({...p,currency:e.target.value}))}>
                <option value="UGX">UGX — Ugandan Shilling</option>
                <option value="USD">USD — US Dollar</option>
                <option value="KES">KES — Kenyan Shilling</option>
              </select>
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:8, alignItems:'center' }}>
            {saved === 'company' && <span style={{ fontSize:12, color:'var(--green)' }}><i className="ti ti-check" /> Saved!</span>}
            <button className="btn primary" onClick={() => save(company,'company')} disabled={saving==='company'}>
              <i className="ti ti-device-floppy" /> {saving==='company' ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {tab === 'notifications' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title"><i className="ti ti-bell" style={{ color:'var(--blue)' }} /> Notification preferences</span>
          </div>
          {[
            { key:'taskAssign',     label:'Task assignment',       desc:'Notify when a task is assigned to a team member' },
            { key:'taskOverdue',    label:'Overdue tasks',          desc:'Daily alert when tasks pass their due date'       },
            { key:'shipmentStatus', label:'Shipment status change', desc:'Notify when a shipment status is updated'         },
            { key:'dailyDigest',    label:'Daily digest',           desc:'Morning summary of all open tasks and shipments'  },
            { key:'weeklyReport',   label:'Weekly report',          desc:'End-of-week summary with completion statistics'   },
          ].map(n => (
            <div key={n.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid var(--gray-200)', gap:12 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>{n.label}</div>
                <div style={{ fontSize:12, color:'var(--gray-500)' }}>{n.desc}</div>
              </div>
              <ToggleSwitch on={!!notifs[n.key]} onChange={() => setNotifs(p=>({...p,[n.key]:!p[n.key]}))} />
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:16, alignItems:'center' }}>
            {saved === 'notifications' && <span style={{ fontSize:12, color:'var(--green)' }}><i className="ti ti-check" /> Saved!</span>}
            <button className="btn primary" onClick={() => save(notifs,'notifications')} disabled={saving==='notifications'}>
              <i className="ti ti-device-floppy" /> {saving==='notifications' ? 'Saving…' : 'Save preferences'}
            </button>
          </div>
        </div>
      )}

      {tab === 'danger' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title" style={{ color:'var(--red)' }}><i className="ti ti-alert-triangle" /> Danger zone</span>
          </div>
          <div className="alert warning" style={{ marginBottom:20 }}>
            <i className="ti ti-alert-triangle" />
            <span>Actions in this section are irreversible. Proceed with caution.</span>
          </div>
          {[
            { label:'Export all data',  desc:'Download all tasks, shipments, and activity logs as Excel.',      btn:'Export',       cls:'btn success', action:() => alert('Export available from the Shipments page.') },
            { label:'Clear all tasks',  desc:'Permanently delete all tasks from the system. Cannot be undone.', btn:'Clear tasks',  cls:'btn danger',  action:() => { if (window.confirm('Delete ALL tasks permanently?')) alert('Contact your system administrator to execute this.'); } },
            { label:'Reset all data',   desc:'Wipe all records and reset to factory state.',                    btn:'Reset system', cls:'btn danger',  action:() => { if (window.confirm('RESET EVERYTHING? This is permanent!')) alert('Contact your system administrator to execute this.'); } },
          ].map(item => (
            <div key={item.label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 0', borderBottom:'1px solid var(--gray-200)', gap:16 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>{item.label}</div>
                <div style={{ fontSize:12, color:'var(--gray-500)' }}>{item.desc}</div>
              </div>
              <button className={item.cls} style={{ flexShrink:0 }} onClick={item.action}>{item.btn}</button>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

// ─── 404 ──────────────────────────────────────────────────────
export function NotFound() {
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, background:'var(--gray-100)' }}>
      <div style={{ fontSize:48, fontWeight:700, color:'var(--gray-300)' }}>404</div>
      <div style={{ fontSize:16, color:'var(--gray-600)' }}>Page not found</div>
      <a href="/" style={{ color:'var(--blue)', fontSize:13 }}>Go home</a>
    </div>
  );
}
